import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Home, LogOut, Award, TrendingUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const moods = [
  { value: 'awesome', emoji: '😄', label: 'Harika', color: '#fef3c7', textColor: '#92400e' },
  { value: 'good', emoji: '🙂', label: 'İyi', color: '#d1fae5', textColor: '#065f46' },
  { value: 'okay', emoji: '😐', label: 'Normal', color: '#dbeafe', textColor: '#1e40af' },
  { value: 'bad', emoji: '😔', label: 'Kötü', color: '#fce7f3', textColor: '#9f1239' },
  { value: 'awful', emoji: '😢', label: 'Çok Kötü', color: '#f3e8ff', textColor: '#6b21a8' },
];

const timeOfDayLabels = {
  morning: 'Sabah',
  afternoon: 'Öğlen',
  evening: 'Akşam',
  night: 'Gece',
  anytime: 'Belirtilmemiş'
};

export default function Statistics({ user, onLogout }) {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [statsRes, achievementsRes] = await Promise.all([
        axios.get(`${API}/moods/statistics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/moods/achievements`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStatistics(statsRes.data);
      setAchievements(achievementsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getMoodEmoji = (value) => {
    return moods.find(m => m.value === value)?.emoji || '';
  };

  const getMoodLabel = (value) => {
    return moods.find(m => m.value === value)?.label || '';
  };

  const getMoodColor = (value) => {
    return moods.find(m => m.value === value)?.textColor || '#6b7280';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="animate-pulse text-lg">Yükleniyor...</div>
      </div>
    );
  }

  const totalMoods = statistics?.total_entries || 0;
  const maxMoodCount = Math.max(...Object.values(statistics?.mood_distribution || {}), 1);
  const maxTimeCount = Math.max(...Object.values(statistics?.time_of_day_distribution || {}), 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">📊</span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">İstatistikler & Başarımlar</h1>
              <p className="text-sm text-gray-600">Ruh hali analizin</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="home-btn"
              variant="outline"
              onClick={() => navigate('/')}
              className="gap-2 hover-lift"
            >
              <Home className="w-4 h-4" />
              Ana Sayfa
            </Button>
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={onLogout}
              className="gap-2 hover-lift"
            >
              <LogOut className="w-4 h-4" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-6 shadow-lg hover-lift animate-scale-in">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Toplam Kayıt</p>
                <p className="text-3xl font-bold text-purple-600">{statistics.total_entries}</p>
              </div>
              <span className="text-4xl">📝</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover-lift animate-scale-in" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Mevcut Seri</p>
                <p className="text-3xl font-bold text-orange-600">{statistics.current_streak}</p>
              </div>
              <span className="text-4xl fire-effect">🔥</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover-lift animate-scale-in" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">En Uzun Seri</p>
                <p className="text-3xl font-bold text-amber-600">{statistics.longest_streak}</p>
              </div>
              <span className="text-4xl">🏆</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover-lift animate-scale-in" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Ortalama Yoğunluk</p>
                <p className="text-3xl font-bold text-blue-600">{statistics.average_intensity}/10</p>
              </div>
              <span className="text-4xl">🎯</span>
            </div>
          </div>
        </div>

        {/* Best and Worst Days */}
        {statistics.best_day && statistics.worst_day && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg hover-lift animate-fade-in">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span className="text-2xl">🌟</span>
                En İyi Günün
              </h3>
              <div className="flex items-center gap-4 p-4 rounded-xl" style={{ backgroundColor: moods.find(m => m.value === statistics.best_day.mood)?.color }}>
                <span className="text-5xl">{getMoodEmoji(statistics.best_day.mood)}</span>
                <div>
                  <p className="font-semibold text-lg">{getMoodLabel(statistics.best_day.mood)}</p>
                  <p className="text-sm opacity-80">{format(parseISO(statistics.best_day.date), 'dd MMMM yyyy', { locale: tr })}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg hover-lift animate-fade-in">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span className="text-2xl">🌧️</span>
                En Zor Günün
              </h3>
              <div className="flex items-center gap-4 p-4 rounded-xl" style={{ backgroundColor: moods.find(m => m.value === statistics.worst_day.mood)?.color }}>
                <span className="text-5xl">{getMoodEmoji(statistics.worst_day.mood)}</span>
                <div>
                  <p className="font-semibold text-lg">{getMoodLabel(statistics.worst_day.mood)}</p>
                  <p className="text-sm opacity-80">{format(parseISO(statistics.worst_day.date), 'dd MMMM yyyy', { locale: tr })}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mood Distribution */}
        <div className="bg-white rounded-2xl p-6 shadow-lg animate-fade-in">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Ruh Hali Dağılımı
          </h3>
          <div className="space-y-4">
            {moods.map((mood) => {
              const count = statistics.mood_distribution[mood.value] || 0;
              const percentage = totalMoods > 0 ? Math.round((count / totalMoods) * 100) : 0;
              const barWidth = maxMoodCount > 0 ? (count / maxMoodCount) * 100 : 0;
              
              return (
                <div key={mood.value} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl emoji-hover">{mood.emoji}</span>
                      <span className="font-medium">{mood.label}</span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {count} gün ({percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                    <div
                      className="h-full rounded-full progress-bar"
                      style={{
                        width: `${barWidth}%`,
                        backgroundColor: mood.textColor
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Time of Day Distribution */}
        <div className="bg-white rounded-2xl p-6 shadow-lg animate-fade-in">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <span className="text-2xl">⏰</span>
            Günün Saatlerine Göre Dağılım
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {Object.entries(statistics.time_of_day_distribution).map(([time, count]) => {
              const percentage = totalMoods > 0 ? Math.round((count / totalMoods) * 100) : 0;
              const timeEmojis = { morning: '🌅', afternoon: '☀️', evening: '🌆', night: '🌙', anytime: '⏰' };
              
              return (
                <div key={time} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 text-center hover-grow">
                  <div className="text-3xl mb-2">{timeEmojis[time]}</div>
                  <div className="text-sm font-medium text-gray-700 mb-1">{timeOfDayLabels[time]}</div>
                  <div className="text-2xl font-bold text-purple-600">{count}</div>
                  <div className="text-xs text-gray-500">({percentage}%)</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Achievements */}
        <div className="bg-white rounded-2xl p-6 shadow-lg animate-fade-in">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <Award className="w-5 h-5" />
            Başarımlar ({achievements.total})
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {achievements.achievements.map((achievement) => (
              <div
                key={achievement.id}
                data-testid={`achievement-${achievement.id}`}
                className={`p-4 rounded-xl border-2 text-center hover-lift ${
                  achievement.earned
                    ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-amber-300 badge-earned'
                    : 'bg-gray-50 border-gray-200 badge-locked'
                }`}
              >
                <div className="text-5xl mb-2">{achievement.icon}</div>
                <div className="font-semibold text-sm mb-1">{achievement.title}</div>
                <div className="text-xs text-gray-600">{achievement.description}</div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}